import {
  NgxMatDatetimepicker,
  NGX_MAT_DATE_FORMATS,
} from '@angular-material-components/datetime-picker';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import moment from 'moment';
import {
  asapScheduler,
  debounceTime,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { DateFormats } from './date-formats';

@Component({
  selector: 'app-date-box',
  templateUrl: 'date-box.component.html',
  styleUrls: ['date-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateBoxComponent,
    },
    DateFormats,
    {
      provide: NGX_MAT_DATE_FORMATS,
      useExisting: DateFormats,
    },
  ],
})
export class DateBoxComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('inputEl', { static: true })
  inputElRef: ElementRef<HTMLInputElement>;

  @ViewChild('picker', { static: true })
  pickerRef: NgxMatDatetimepicker<Date>;

  get inputEl() {
    return this.inputElRef?.nativeElement;
  }

  private formEl: HTMLFormElement;
  private formResetSubscription: Subscription;

  @Input()
  type: 'date' | 'datetime' = 'date';

  @Input()
  value: Date;
  @Output()
  valueChange = new EventEmitter<Date>();

  @Output()
  onReset = new EventEmitter<any>();
  @Output()
  onInput = new EventEmitter<any>();
  @Output()
  onFocus = new EventEmitter<any>();

  @Output()
  onKeydown = new EventEmitter<any>();
  @Output()
  onClearClick = new EventEmitter<any>();

  private onChange = (value) => {};

  private onTouched = () => {};

  @Input()
  name: string;

  @Input()
  placeholder: string;

  @Input()
  readOnly: boolean;

  @Input()
  get disabled() {
    return this.formControl.disabled;
  }

  set disabled(value) {
    value = !!value;
    if (value === this.disabled) {
      return;
    }

    if (value) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }

    this.changeDetector.detectChanges();
  }

  @Input()
  max: Date;

  @Input()
  showErrorEnabled: boolean = true;
  @Input()
  showClearButton: boolean = true;
  @Input()
  showPickerIndicator: boolean = true;

  @Input()
  label: string;

  focused = false;
  touched = false;

  get empty() {
    return !this.inputEl?.value && !this._isBadInput();
  }

  formControl: FormControl<Date>;

  private lifecycle = new Subject<void>();

  constructor(
    formBuilder: FormBuilder,
    private dateFormats: DateFormats,
    private changeDetector: ChangeDetectorRef
  ) {
    this.formControl = formBuilder.control<Date>(null);
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.onInputChange();
      });

    let parentEl = this.inputEl.parentElement;
    while (parentEl && !(parentEl instanceof HTMLFormElement)) {
      parentEl = parentEl.parentElement;
    }

    if (parentEl instanceof HTMLFormElement) {
      this.formEl = parentEl;
      fromEvent(this.formEl, 'reset')
        .pipe(debounceTime(0, asapScheduler), takeUntil(this.lifecycle))
        .subscribe(() => {
          this.onInputChange();

          this.onReset.emit();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.type) {
      this.dateFormats.showTimeFormat = this.type !== 'date';
    }

    if (changes.value) {
      this._setInputValue();
    }
  }

  ngAfterViewInit(): void {
    this._setInputValue();
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  writeValue(value: Date): void {
    value = this.handleValue(value);

    if (!this.isEquals(this.value, value)) {
      this.value = value;
      this._setInputValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocusChanged(focus) {
    this.focused = focus;

    this.onFocus.emit(focus);
  }

  onInputChange() {
    this.formResetSubscription?.unsubscribe();

    let value = this._getValueFromInput();

    if (value instanceof Date) {
      if (this.max instanceof Date && value.getTime() > this.max.getTime()) {
        value = new Date(this.max);
      }
    }

    if (!this.isEquals(this.value, value)) {
      this.value = value;
      this._setInputValue();

      this.valueChange.emit(value);
      this.onChange?.(value);
    }
  }

  onInputValue(event) {
    this.onInputChange();

    this.onInput.emit({ event, component: this });
  }

  onInputKeydown(event) {
    this.onKeydown.emit({ event, component: this });
  }

  onBtnClearClick(e) {
    e?.stopPropagation();
    this.onClearClick.emit();

    this.formControl.reset();
    this.onInputChange();
  }

  onBtnPickerIndicatorClick(e) {
    e?.stopPropagation();

    this.showPicker();
  }

  @HostListener('click')
  onContainerClick() {
    if (!this.focused && !this.disabled && !this.readOnly) {
      this.focused = true;
      this.inputEl.focus();

      if (!this.touched) {
        this.touched = true;
        this.onTouched?.();
      }
    }
  }

  showPicker() {
    this.pickerRef?.open();
  }

  hidePicker() {
    this.pickerRef?.close();
  }

  protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let validity = (this.inputElRef?.nativeElement as HTMLInputElement)
      ?.validity;
    return validity && validity.badInput;
  }

  private handleValue(value: Date | string | number): Date {
    if (value && !(value instanceof Date)) {
      value = new Date(value);
    }

    return (value ?? null) as Date;
  }

  private _getValueFromInput() {
    let value = this.formControl.getRawValue();
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.toDate();
    }

    value = new Date(value);

    if (this.type === 'date') {
      value.setHours(12, 0, 0, 0);
    }

    return value;
  }

  private _setInputValue() {
    const value = this.handleValue(this.value);
    const currentValue = this._getValueFromInput();
    if (!this.isEquals(value, currentValue)) {
      this.formControl.reset(value);

      this.changeDetector.detectChanges();
    }
  }

  private isEquals(d1: Date, d2: Date) {
    return d1?.getTime() === d2?.getTime();
  }
}
